import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Clock, Check, PauseCircle, PlayCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const pad = (n) => String(n).padStart(2, "0");

/** Per-SSP automated fetch schedule: time in UTC + optional repeat interval. */
export const SspScheduleEditor = ({ token, schedule, onSaved }) => {
  const [time, setTime] = useState(`${pad(schedule.hour)}:${pad(schedule.minute)}`);
  const [interval, setIntervalHours] = useState(schedule.interval_hours || 0);
  const [saving, setSaving] = useState(false);

  const key = schedule.key;
  const dirty =
    time !== `${pad(schedule.hour)}:${pad(schedule.minute)}` ||
    Number(interval || 0) !== (schedule.interval_hours || 0);

  const save = async (enabled = schedule.enabled) => {
    const [h, m] = time.split(":").map((v) => parseInt(v, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) { toast.error("Enter a valid time"); return; }
    const iv = Number(interval || 0);
    if (iv && (iv < 1 || iv > 23)) { toast.error("Repeat must be 1-23 hours (0 = once a day)"); return; }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/reporting/marketplace/ssp-schedules/${key}`,
        { hour: h, minute: m, interval_hours: iv, enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSaved(res.data);
      toast.success(`${res.data.ssp_source}: ${res.data.description}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5" data-testid={`schedule-${key}`}>
      <div className="flex items-center justify-between text-[10px] font-mono text-[#ebebeb]/40">
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Auto fetch (UTC)</span>
        <button
          onClick={() => save(!schedule.enabled)}
          disabled={saving}
          className={`flex items-center gap-1 transition-colors ${schedule.enabled ? "text-[#22c55e] hover:text-[#22c55e]/70" : "text-[#ebebeb]/35 hover:text-[#ebebeb]/60"}`}
          title={schedule.enabled ? "Pause automated fetching" : "Resume automated fetching"}
          data-testid={`schedule-toggle-${key}`}
        >
          {schedule.enabled ? <><PlayCircle className="w-3 h-3" /> on</> : <><PauseCircle className="w-3 h-3" /> paused</>}
        </button>
      </div>
      <div className="flex items-center gap-1.5">
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="flex-1 min-w-0 bg-[#15171a] border border-[#2a2d31] rounded px-2 py-1.5 text-[11px] font-mono text-[#ebebeb] focus:outline-none focus:border-[#3b82f6]"
          data-testid={`schedule-time-${key}`}
        />
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[10px] font-mono text-[#ebebeb]/35">every</span>
          <input
            type="number"
            min={0}
            max={23}
            value={interval}
            onChange={(e) => setIntervalHours(e.target.value)}
            className="w-11 bg-[#15171a] border border-[#2a2d31] rounded px-1.5 py-1.5 text-[11px] font-mono text-[#ebebeb] text-center focus:outline-none focus:border-[#3b82f6]"
            data-testid={`schedule-interval-${key}`}
          />
          <span className="text-[10px] font-mono text-[#ebebeb]/35">h</span>
        </div>
        <button
          onClick={() => save()}
          disabled={saving || !dirty}
          className="shrink-0 flex items-center gap-1 px-2 py-1.5 rounded text-[10px] font-mono border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10 disabled:opacity-30 transition-colors"
          data-testid={`schedule-save-${key}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
        </button>
      </div>
      <p className="text-[10px] font-mono text-[#ebebeb]/35" data-testid={`schedule-next-${key}`}>
        {schedule.enabled
          ? `${schedule.description} · next ${schedule.next_run ? new Date(schedule.next_run).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}`
          : "Automated fetching paused"}
      </p>
    </div>
  );
};

export default SspScheduleEditor;
